import type { EmailTemplateName } from "./index";

export const sampleData: Record<EmailTemplateName, Record<string, unknown>> = {
  invitation: {
    inviteUrl: "https://app.example.com/invite/abc123",
    orgName: "Medizinischer Dienst Berlin-Brandenburg",
    role: "Gutachter",
    inviterName: "Dr. Maria Schmidt",
  },
  welcome: {
    orgName: "Medizinischer Dienst Berlin-Brandenburg",
    userName: "Dr. Thomas Müller",
    loginUrl: "https://app.example.com/login",
  },
  passwordReset: {
    resetUrl: "https://app.example.com/reset/xyz789",
    userName: "Dr. Thomas Müller",
    expiresInMinutes: 60,
  },
  accountLocked: {
    supportEmail: "support@md-bb.jourfx.app",
  },
  licenseExpiring: {
    orgName: "Medizinischer Dienst Berlin-Brandenburg",
    expiresAt: "15.03.2026",
    daysRemaining: "7",
    renewalUrl: "https://admin.example.com/licenses",
  },
  licenseSuspended: {
    orgName: "Medizinischer Dienst Berlin-Brandenburg",
    supportEmail: "support@md-bb.jourfx.app",
  },
  appointment_confirmation: {
    appointment: {
      scheduledAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      duration: 1800000, // 30 minutes
    },
    versicherter: { name: "Max Mustermann" },
    organization: { name: "Medizinischer Dienst Berlin-Brandenburg" },
    joinUrl: "https://portal.example.com/join/abc123",
  },
  appointment_reminder_7d: {
    appointment: {
      scheduledAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      accessCode: "AB12-CD34",
    },
    versicherter: { name: "Max Mustermann" },
    organization: { name: "Medizinischer Dienst Berlin-Brandenburg" },
    joinUrl: "https://portal.example.com/join/abc123",
  },
  appointment_reminder_24h: {
    appointment: {
      scheduledAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      accessCode: "AB12-CD34",
    },
    versicherter: { name: "Max Mustermann" },
    organization: { name: "Medizinischer Dienst Berlin-Brandenburg" },
    joinUrl: "https://portal.example.com/join/abc123",
  },
  appointment_cancelled: {
    appointment: {
      scheduledAt: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
      accessCode: "AB12-CD34",
    },
    versicherter: { name: "Max Mustermann" },
    organization: { name: "Medizinischer Dienst Berlin-Brandenburg" },
  },
  appointment_rescheduled: {
    appointment: {
      scheduledAt: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
      accessCode: "AB12-CD34",
    },
    versicherter: { name: "Max Mustermann" },
    organization: { name: "Medizinischer Dienst Berlin-Brandenburg" },
    joinUrl: "https://portal.example.com/join/abc123",
  },
  video_guest_invitation: {
    guestName: "Max Mustermann",
    inviterName: "Dr. Maria Schmidt",
    inviterRole: "gutachter",
    organization: { name: "Medizinischer Dienst Berlin-Brandenburg" },
    appointment: {
      scheduledAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      duration: 45,
      timezone: "Europe/Berlin",
    },
    joinUrl: "https://portal.example.com/join/guest-abc123",
  },
  consultation_invitation: {
    organization: { name: "Medizinischer Dienst Berlin-Brandenburg" },
    versichertenName: "Max Mustermann",
    gutachterName: "Dr. Maria Schmidt",
    bookingUrl: "https://portal.example.com/?next=%2Fbooking%2Finv-abc123&code=ABC-123-XYZ",
    accessCode: "ABC-123-XYZ",
    expiresAt: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
  },
  password_changed: {
    userName: "Dr. Thomas Müller",
  },
  no_show_alert: {
    organization: { name: "Medizinischer Dienst Berlin-Brandenburg" },
    versichertenName: "Max Mustermann",
    appointment: {
      scheduledAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    },
  },
  licenseExpired: {
    orgName: "Medizinischer Dienst Berlin-Brandenburg",
    expiredAt: "01.02.2026",
    supportEmail: "support@md-bb.jourfx.app",
  },
  email_verification: {
    verifyUrl: "https://app.example.com/verify/tok-abc123",
    userName: "Dr. Thomas Müller",
  },
  registration_confirmation: {
    userName: "Anna Muster",
    orgName: "Medizinischer Dienst Berlin-Brandenburg",
    registrationUrl: "https://portal.example.com/registrierung",
    supportEmail: "support@md-bb.jourfx.app",
  },
  self_registration_notification: {
    reasonLabel: "Pflegebegutachtung",
    organizationName: "Medizinischer Dienst Berlin-Brandenburg",
    versicherter: {
      firstName: "Anna",
      lastName: "Muster",
      dateOfBirth: "1988-11-04",
      email: "anna.muster@example.com",
      phone: "+49 170 1234567",
    },
  },
};

export const templateGroups = [
  {
    label: "Konto",
    key: "account",
    templates: ["invitation", "welcome", "passwordReset", "accountLocked", "password_changed", "email_verification", "registration_confirmation", "self_registration_notification"] as EmailTemplateName[],
  },
  {
    label: "Lizenz",
    key: "license",
    templates: ["licenseExpiring", "licenseSuspended", "licenseExpired"] as EmailTemplateName[],
  },
  {
    label: "Termin",
    key: "appointment",
    templates: [
      "appointment_confirmation",
      "appointment_reminder_7d",
      "appointment_reminder_24h",
      "appointment_cancelled",
      "appointment_rescheduled",
      "video_guest_invitation",
      "consultation_invitation",
      "no_show_alert",
    ] as EmailTemplateName[],
  },
];

export const templateLabels: Record<EmailTemplateName, string> = {
  invitation: "Einladung",
  welcome: "Willkommen",
  passwordReset: "Passwort zurücksetzen",
  accountLocked: "Konto gesperrt",
  licenseExpiring: "Lizenz läuft ab",
  licenseSuspended: "Lizenz ausgesetzt",
  appointment_confirmation: "Terminbestätigung",
  appointment_reminder_7d: "Erinnerung (7 Tage)",
  appointment_reminder_24h: "Erinnerung (24 Stunden)",
  appointment_cancelled: "Termin abgesagt",
  appointment_rescheduled: "Termin verschoben",
  video_guest_invitation: "Gast-Einladung zur Videosprechstunde",
  consultation_invitation: "Terminbuchung (Einladung)",
  password_changed: "Passwort geändert",
  no_show_alert: "Nichterscheinen",
  licenseExpired: "Lizenz abgelaufen",
  email_verification: "E-Mail bestätigen",
  registration_confirmation: "Registrierung bestätigt",
  self_registration_notification: "Selbstregistrierung Benachrichtigung",
};
